import { API_CONFIG } from '@/config/api';
import { httpClient } from '@/utils/httpClient';

export interface UploadTransactionDocumentResponse {
  uploadedDocumentId: string;
  documentEntityId: string | null;
  transactionId: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  downloadUrl: string;
}

const MAX_FILE_BYTES = 25 * 1024 * 1024; // 25MB

const ALLOWED_MIME_TYPES = new Set([
  'application/pdf',
  'image/jpeg',
  'image/png',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // docx
]);

class TransactionDocumentService {
  private getBasePath(): string {
    return `${API_CONFIG.ACTIONS_API_BASE_PATH}/transactions`;
  }

  getDocumentDownloadUrl(documentId: string): string {
    return `${API_CONFIG.BASE_URL}${this.getBasePath()}/documents/${documentId}`;
  }

  async uploadDocument(
    transactionId: string,
    file: File,
  ): Promise<UploadTransactionDocumentResponse> {
    if (!transactionId) {
      throw new Error('Transaction ID is required.');
    }

    if (!ALLOWED_MIME_TYPES.has(file.type)) {
      throw new Error('Only PDF, JPEG, PNG, and DOCX files are supported.');
    }

    if (file.size === 0) {
      throw new Error('File must not be empty.');
    }

    if (file.size > MAX_FILE_BYTES) {
      throw new Error('File exceeds maximum allowed size of 25MB.');
    }

    const base64 = await this.readFileAsBase64(file);

    return httpClient.post<UploadTransactionDocumentResponse>(
      `${this.getBasePath()}/${transactionId}/documents/upload`,
      {
        base64,
        fileName: file.name,
        mimeType: file.type,
      },
    );
  }

  private readFileAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        const result = reader.result;
        if (typeof result === 'string') {
          const [, base64] = result.split(',');
          resolve(base64 || '');
          return;
        }
        reject(new Error('Failed to read file as base64.'));
      };

      reader.onerror = () => {
        reject(new Error('Failed to read file.'));
      };

      reader.readAsDataURL(file);
    });
  }
}

export const transactionDocumentService = new TransactionDocumentService();
